import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { StaffObservationEntry, TransactionDetail } from '../../../models/transaction';
import { AccountService } from '../../../services/account.service';
import { resolveTransactionTypeLabel, TransactionService } from '../../../services/transaction.service';
import { AdminPageHeaderComponent } from '../../shared/admin-page-header/admin-page-header.component';
import { resolveInitialWorkflowDraft } from './transaction-detail-workflow.util';
import { UserAlertService } from '../../../services/user-alert.service';

export interface StaffObservationRowModel {
  entry: StaffObservationEntry;
  draft: string;
}

@Component({
  selector: 'app-transaction-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    DialogModule,
    ProgressSpinnerModule,
    AdminPageHeaderComponent,
  ],
  templateUrl: './transaction-detail.component.html',
  styleUrl: './transaction-detail.component.scss',
})
export class TransactionDetailComponent implements OnInit {
  readonly resolveTransactionTypeLabel = resolveTransactionTypeLabel;
  private readonly staffObservationsEnabled = false;

  detail: TransactionDetail | null = null;
  isLoading = false;
  errorMessage = '';
  actionMessage = '';
  actionError = '';

  rejectDialogVisible = false;
  rejectReason = '';
  receiptDialogVisible = false;
  approvePaymentDialogVisible = false;
  receiptFile: File | null = null;

  workflowStageDraft = '';
  staffObservationRows: StaffObservationRowModel[] = [];
  private readonly savingStaffEntryIds = new Set<string>();
  trustedDraft = false;
  trustedRevokeReason = '';
  isActionPending = false;
  isTrustedSavePending = false;

  readonly workflowStageOptions = [
    { label: 'Solicitado', value: 'REQUESTED' },
    { label: 'Em revisão', value: 'IN_REVIEW' },
    { label: 'Aprovado (saque)', value: 'APPROVED' },
    { label: 'Pago', value: 'PAID' },
    { label: 'Rejeitado (saque)', value: 'REJECTED' },
  ];

  private transactionId: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private transactionService: TransactionService,
    public accountService: AccountService,
    private userAlertService: UserAlertService
  ) { }

  displayNoteModal = false;
  editingNoteId: number | null = null;
  newNoteText = '';
  isSavingNote = false;

  get sortedNotes(): any[] {
    if (!this.detail?.notes?.length) return [];
    return [...this.detail.notes].sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const idParam = params.get('id')?.trim();
      if (!idParam) {
        this.errorMessage = 'ID de transação inválido.';
        return;
      }
      this.transactionId = idParam;
      this.loadDetail();
    });
  }

  get canEditWorkflowStage(): boolean {
    return this.accountService.canEditCashOutWorkflowStage();
  }

  get canEditStaffObservations(): boolean {
    return this.staffObservationsEnabled && this.accountService.canEditStaffObservations();
  }

  get canEditTrusted(): boolean {
    return (
      this.accountService.canEditAccountTrusted() && this.accountIdForUserLink != null
    );
  }

  get canRunCashOutPaymentActions(): boolean {
    return this.accountService.canRunCashOutPaymentActions();
  }

  get trustedDirty(): boolean {
    if (this.detail == null) {
      return false;
    }
    const current = this.detail.is_trusted === true;
    return this.trustedDraft !== current;
  }

  get accountIdForUserLink(): number | null {
    const d = this.detail;
    if (!d) return null;
    const id = d.account_id ?? d.account?.id;
    return id != null ? id : null;
  }

  detailHasRisk(d: TransactionDetail): boolean {
    return d.has_open_risk === true || d.has_risk === true;
  }

  isCashOutTransaction(d: TransactionDetail | null | undefined): boolean {
    if (!d) {
      return false;
    }
    const txType = String(d.transaction_type ?? d.type ?? '')
      .trim()
      .toUpperCase();
    return txType === 'CASH_OUT';
  }

  loadDetail(): void {
    if (this.transactionId == null) return;
    this.isLoading = true;
    this.errorMessage = '';
    this.actionMessage = '';
    this.actionError = '';

    this.transactionService.getTransaction(this.transactionId).subscribe({
      next: (d) => {
        this.detail = d;
        this.workflowStageDraft = resolveInitialWorkflowDraft(d, this.workflowStageOptions);
        this.rebuildStaffObservationRows(d);
        this.trustedDraft = d.is_trusted === true;
        this.trustedRevokeReason = '';
        this.isLoading = false;
      },
      error: () => {
        this.detail = null;
        this.errorMessage = 'Não foi possível carregar a transação.';
        this.isLoading = false;
      },
    });
  }

  goBack(): void {
    this.router.navigate(['/dashboard/transactions']);
  }

  goUserTransactions(): void {
    const id = this.accountIdForUserLink;
    if (id != null && this.transactionId != null) {
      this.router.navigate(['/dashboard/transactions'], {
        queryParams: {
          search: String(id),
          page: 1,
          from_transaction: this.transactionId,
        },
      });
    }
  }

  saveTrusted(): void {
    const accId = this.accountIdForUserLink;
    if (accId == null || !this.canEditTrusted || !this.trustedDirty) {
      return;
    }
    const revoking =
      this.detail?.is_trusted === true && this.trustedDraft === false;
    if (revoking && !this.trustedRevokeReason.trim()) {
      this.actionError = 'Indique o motivo da revogação do trusted.';
      this.actionMessage = '';
      return;
    }
    this.isTrustedSavePending = true;
    this.actionError = '';
    this.actionMessage = '';
    this.transactionService
      .setAccountTrusted(
        accId,
        this.trustedDraft,
        revoking ? this.trustedRevokeReason : undefined
      )
      .pipe(finalize(() => (this.isTrustedSavePending = false)))
      .subscribe({
        next: () => {
          this.actionMessage = 'Estado trusted atualizado.';
          this.trustedRevokeReason = '';
          this.loadDetail();
        },
        error: () => {
          this.actionError = 'Não foi possível atualizar o estado trusted.';
        },
      });
  }

  applyWorkflowStage(): void {
    if (!this.canEditWorkflowStage || this.transactionId == null || !this.workflowStageDraft) return;
    this.runAction(
      this.transactionService.updateWorkflowStage(
        this.transactionId!,
        this.workflowStageDraft
      )
    );
  }

  trackStaffRow(_index: number, row: StaffObservationRowModel): string {
    return row.entry.id;
  }

  isNewStaffObservation(row: StaffObservationRowModel): boolean {
    return row.entry.id.startsWith('new-');
  }

  isStaffRowDirty(row: StaffObservationRowModel): boolean {
    return row.draft.trim() !== row.entry.text.trim();
  }

  isSavingStaffRow(row: StaffObservationRowModel): boolean {
    return this.savingStaffEntryIds.has(row.entry.id);
  }

  addStaffObservationDraft(): void {
    this.staffObservationRows = [
      ...this.staffObservationRows,
      {
        entry: {
          id: `new-${Date.now()}`,
          text: '',
          author: null,
          created_at: null,
          updated_at: null,
        },
        draft: '',
      },
    ];
  }

  removeStaffObservationDraft(row: StaffObservationRowModel): void {
    if (!this.isNewStaffObservation(row)) {
      return;
    }
    this.staffObservationRows = this.staffObservationRows.filter((r) => r !== row);
  }

  saveStaffObservationRow(row: StaffObservationRowModel): void {
    if (!this.canEditStaffObservations || this.transactionId == null) {
      return;
    }
    const text = row.draft.trim();
    if (!text || !this.isStaffRowDirty(row)) {
      return;
    }
    const entryId = row.entry.id;
    if (this.savingStaffEntryIds.has(entryId)) {
      return;
    }

    this.actionError = '';
    this.actionMessage = '';

    const isNew = this.isNewStaffObservation(row);
    const request$: Observable<unknown> = isNew
      ? this.transactionService.postStaffObservation(this.transactionId, text)
      : this.transactionService.patchStaffObservationEntry(this.transactionId, entryId, text);

    this.savingStaffEntryIds.add(entryId);
    request$.pipe(finalize(() => this.savingStaffEntryIds.delete(entryId))).subscribe({
      next: () => {
        this.actionMessage = isNew ? 'Observação adicionada.' : 'Observação atualizada.';
        this.loadDetail();
      },
      error: () => {
        this.actionError = isNew
          ? 'Não foi possível salvar a observação.'
          : 'Não foi possível atualizar a observação.';
      },
    });
  }

  private rebuildStaffObservationRows(d: TransactionDetail): void {
    let entries: StaffObservationEntry[] = [];
    if (d.staff_observation_entries?.length) {
      entries = d.staff_observation_entries.map((e) => ({ ...e }));
    } else if (d.staff_observations?.trim()) {
      entries = [
        {
          id: 'legacy',
          text: d.staff_observations.trim(),
          author: null,
          created_at: null,
          updated_at: null,
        },
      ];
    }
    entries.sort((a, b) => {
      const ta = a.created_at ?? '';
      const tb = b.created_at ?? '';
      return tb.localeCompare(ta);
    });
    this.staffObservationRows = entries.map((entry) => ({
      entry,
      draft: entry.text,
    }));
  }

  confirmApprove(): void {
    if (!this.canRunCashOutPaymentActions || this.transactionId == null) return;
    this.approvePaymentDialogVisible = true;
  }

  submitApprovePayment(): void {
    if (!this.canRunCashOutPaymentActions || this.transactionId == null) return;
    this.approvePaymentDialogVisible = false;
    this.runAction(this.transactionService.approvePayment(this.transactionId!));
  }

  formatCurrency(value?: number | string | null): string {
    const amount = Number(value ?? 0);
    if (!Number.isFinite(amount)) {
      return '-';
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  }

  openRejectDialog(): void {
    this.rejectReason = '';
    this.rejectDialogVisible = true;
  }

  submitReject(): void {
    const reason = this.rejectReason.trim();
    if (!reason || this.transactionId == null || !this.canRunCashOutPaymentActions) return;
    this.rejectDialogVisible = false;
    this.runAction(
      this.transactionService.rejectPayment(this.transactionId!, reason)
    );
  }

  openReceiptDialog(): void {
    this.receiptFile = null;
    this.receiptDialogVisible = true;
  }

  onReceiptSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.receiptFile = file ?? null;
  }

  submitReceipt(): void {
    if (!this.receiptFile || this.transactionId == null || !this.canRunCashOutPaymentActions)
      return;
    this.receiptDialogVisible = false;
    this.runAction(
      this.transactionService.markPaidWithReceipt(
        this.transactionId!,
        this.receiptFile!
      )
    );
  }

  private runAction(req$: Observable<void>): void {
    this.isActionPending = true;
    this.actionError = '';
    this.actionMessage = '';
    req$.pipe(finalize(() => (this.isActionPending = false))).subscribe({
      next: () => {
        this.actionMessage = 'Ação concluída.';
        this.loadDetail();
      },
      error: () => {
        this.actionError = 'Falha na ação. Verifique a API ou tente novamente.';
      },
    });
  }

  openNoteModal(): void {
    this.editingNoteId = null;
    this.newNoteText = '';
    this.displayNoteModal = true;
  }

  openEditNoteModal(note: any): void {
    this.editingNoteId = note.id;
    this.newNoteText = note.description;
    this.displayNoteModal = true;
  }

  closeNoteModal(): void {
    this.displayNoteModal = false;
    this.editingNoteId = null;
    this.newNoteText = '';
  }

  saveNote(): void {
    const accId = this.accountIdForUserLink;
    if (!accId || !this.newNoteText.trim()) return;

    this.isSavingNote = true;
    const request$ = this.editingNoteId
      ? this.userAlertService.updateAccountNote(this.editingNoteId, this.newNoteText.trim())
      : this.userAlertService.createAccountNote(accId, this.newNoteText.trim());

    request$.subscribe({
      next: () => {
        this.isSavingNote = false;
        this.closeNoteModal();
        this.actionMessage = this.editingNoteId ? 'Observação atualizada com sucesso!' : 'Observação adicionada com sucesso!';
        this.loadDetail();
      },
      error: () => {
        this.isSavingNote = false;
        this.actionError = 'Erro ao salvar a observação.';
      }
    });
  }

  deleteNote(noteId: number): void {
    if (confirm('Tem certeza que deseja deletar esta observação?')) {
      this.userAlertService.deleteAccountNote(noteId).subscribe({
        next: () => {
          this.actionMessage = 'Observação deletada com sucesso!';
          this.loadDetail();
        },
        error: () => {
          this.actionError = 'Erro ao deletar a observação.';
        }
      });
    }
  }
}
